import React, {useState, useMemo} from 'react';
import classnames from 'classnames/bind';
import PropTypes from 'prop-types';
import { connect } from 'react-redux'
import _debounce from 'lodash.debounce'

import PopUp from 'js/components/PopupRedesign';
import Button from 'js/components/shared/Button';
import CustomRangeSelector from 'js/components/shared/FormElements/CustomRangeSelector';

import {useUrl} from 'js/url';
import * as c from '../constants';
import config from '../config'
import AmenitiesSelect from './AmenitiesSelect'
import SortSelect from './SortSelect';

import styles from './FilterDetailedModal.module.scss';

const cx = classnames.bind(styles);

const amenitiesPlus = [
  c.FIELD_DISABLED_PEOPLE,
  c.FIELD_PETS_ALLOWED,
]
const amenitiesEquipment = [
  c.FIELD_PARKING,
  c.FIELD_AIR_CONDITIONER,
  c.FIELD_POOL,
  c.FIELD_SPORTS_HALL,
  c.FIELD_MEETING_ROOM,
]
const resultsCountWordingSingle = window.drupalSettings?.widgets?.filters?.availabilityText
const resultsCountWordingMultiple = window.drupalSettings?.widgets?.filters?.availabilityTextPlural

const FilterDetailedModal = (props) => {
  const {
    title,
    applyTitle,
    clearLabel,
    onClose,
    titles: {
      eventTracking: {
        hotelType: eventTrackingDataHotelType,
        amenity: eventTrackingDataAmenity,
        extra_touches: eventTrackingDataExtraTouches,
      },
      hotelType: hotelTypeTitles,
      amenities: {
        title: amenitiesTitle,
        icons: amenitiesTitles,
        equipment,
      },
      priceRange: {
        currency,
        title: priceRangeTitle,
        min: minPriceValue = 50,
        max: maxPriceValue = 150,
        lowPriceDynamic = '',
        highPriceDynamic = '',
        lowLabel: lowPriceLabel = '',
        highLabel: highPriceLabel = '',
      },
    },
    sortTitles: {
      title: sortSectionTitle,
    },
    more,
    sort,
    sortTitles,
    submitDisabled,
    resultsCount: defaultResultsCount,
    makePrediction,
  } = props;

  const [resultsCount, setResultsCount] = useState(defaultResultsCount);
  const {setParam, applyChanges, discardChanges, getPredictedQueryString} = useUrl();
  const [isLoading, setIsLoading] = useState(false);
  const predictResultsLengthInfo = useMemo(
    () => resultsCount > 1 ? resultsCountWordingMultiple : resultsCountWordingSingle,
    [resultsCount]
  );

  const closeModal = function () {
    discardChanges();
    onClose && onClose();
  }

  const onClearBtn = async function () {
    setParam({
      priceRange: {},
      amenities: [],
      hotelType: [],
      sort: undefined,
    })
  }

  const onModalSubmit = function () {
    applyChanges();
    onClose();
  }

  const onPriceSelectorChange = _debounce(async function (value) {
    const [min, max] = value || [];
    setParam({
      priceRange: {min, max}
    })
    predictResults();
  }, 500)

  const onAmenityChange = async function (value) {
    setParam({
      amenities: value,
    });
    predictResults();
  }

  const onHotelTypeChange = async function (value) {
    setParam({
      hotelType: value,
    });
    predictResults();
  }

  const onSortChange = function (value) {
    setParam({
      sort: value,
    })
  }

  const getPriceValueLabel = function (price, isHigh) {
    if (price >= maxPriceValue && highPriceDynamic) {
      return highPriceDynamic + currency;
    }
    if (price <= minPriceValue && lowPriceDynamic) {
      return lowPriceDynamic + currency;
    }

    return `${isHigh ? highPriceLabel : lowPriceLabel} ${price}${currency}`
  }

  const getMinLabelValue = (value) => getPriceValueLabel(value, false)
  const getMaxLabelValue = (value) => getPriceValueLabel(value, true)

  const hotelTypeInputs = useMemo(function () {
    const hotelTypesData = config.hotelTypes;
    return Object.keys(hotelTypeTitles?.icons || {}).map(key => ({
      id: hotelTypesData[key]?.value,
      icon: hotelTypesData[key]?.icon,
      text: hotelTypeTitles.icons[key],
    }))
  }, [hotelTypeTitles])

  const amenitiesEquipmentInputs = useMemo(function () {
    const amenitiesData = config.amenities;
    return amenitiesEquipment.map(key => ({
      id: amenitiesData[key]?.value,
      icon: amenitiesData[key]?.icon,
      text: amenitiesTitles[key],
    }))
  }, [amenitiesEquipment])

  const amenitiesPlusInputs = useMemo(function () {
    const amenitiesData = config.amenities
    return amenitiesPlus.map(key => ({
      id: amenitiesData[key]?.value,
      icon: amenitiesData[key]?.icon,
      text: amenitiesTitles[key],
    }))
  }, [amenitiesPlus])

  const predictResults = async function () {
    setIsLoading(true);
    const predictQueryString = getPredictedQueryString();
    const resp = await makePrediction(predictQueryString);
    setResultsCount(resp?.results?.length);
    setIsLoading(false);
  }

  return (
    <PopUp
      theme="mobile-full"
      className={cx('modal')}
      closeClassName={cx('modal-close')}
      onClose={closeModal}
    >
      <div className={cx('root')}>
        <div className={cx('header')}>
          <p className={cx('title')}>
            <i className={cx('icon-filtering-icon')} />
            {title}
          </p>
          {isLoading && (<span className={cx('loading-subtitle', 'loading-animation')} />)}
          {!isLoading && (<p className={cx('mobile-subtitle')}>
            <span>{resultsCount}</span>
            {predictResultsLengthInfo}
          </p>)}
        </div>
        <div className={cx('sections-wrap')}>
          <div className={cx('sections')}>
            <div className={cx('section')}>
              <p className={cx('section-title')}>{sortSectionTitle}</p>
              <SortSelect
                value={sort}
                onChange={onSortChange}
                titles={sortTitles}
              />
            </div>
            <div className={cx('section')}>
              <p className={cx('section-title')}>{priceRangeTitle}</p>
              <CustomRangeSelector
                name="price_range_dynamic"
                className={cx('price-selector')}
                onChange={onPriceSelectorChange}
                value={more.priceRange}
                min={minPriceValue}
                max={maxPriceValue}
                step={1}
                minLabel={getMinLabelValue}
                maxLabel={getMaxLabelValue}
              />
            </div>
            <div className={cx('section')}>
              <p className={cx('section-title')}>{amenitiesTitle}</p>
              <AmenitiesSelect
                onChange={onAmenityChange}
                value={more?.amenities}
                options={amenitiesPlusInputs}
                multiple
              />
            </div>
            <div className={cx('section')}>
              <p className={cx('section-title')}>{hotelTypeTitles?.title}</p>
              <AmenitiesSelect
                onChange={onHotelTypeChange}
                value={more?.hotelType}
                options={hotelTypeInputs}
                multiple
              />
            </div>
            <div className={cx('section')}>
              <p className={cx('section-title')}>{equipment}</p>
              <AmenitiesSelect
                onChange={onAmenityChange}
                value={more?.amenities}
                options={amenitiesEquipmentInputs}
                multiple
              />
            </div>
          </div>
        </div>
        <div className={cx('footer')}>
          <button className={cx('reset')} type="reset" onClick={onClearBtn}>{clearLabel}</button>
          {(!isLoading) && (
            <span className={cx('predict')}>
              <span>{resultsCount}</span>
              {predictResultsLengthInfo}
            </span>)}
          {isLoading && (<span className={cx('loading-predict', 'loading-animation')} />)}
          <Button
            disabled={isLoading || submitDisabled || !resultsCount}
            className={cx('submit')}
            type="submit"
            theme="squared"
            color="black"
            onClick={onModalSubmit}
          >
            {applyTitle}
          </Button>
        </div>
      </div>
    </PopUp>
  )
}

FilterDetailedModal.propTypes = {
  className: PropTypes.string,
}

export default connect(
  ({url}) => ({
    more: {
      hotelType: url.uiParams.hotelType,
      priceRange: url.uiParams.priceRange,
      amenities: url.uiParams.amenities,
    },
    sort: url.uiParams.sort,
  })
)(FilterDetailedModal)
